import { useState, useRef, useEffect } from 'react';
import { Card, CardBody, Input, Button, Select, SelectItem, Textarea } from '@nextui-org/react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { Icon } from '@iconify/react';
import {
  generateIntegration,
  fileToBase64,
  type ExcelExample,
} from 'common/src/api/generate-integration';
import {
  processExcelFile,
  type SettlementDetail,
} from 'common/src/api/excel-process';
import { getAllExporters } from 'common/src/api/exporters';
import { getAllSpecies } from 'common/src/api/species';
import type { Exporter, Species } from 'common/src/utils/types';
import UploadSettlementDialog from '../../../components/settlements/upload-settlement-dialog';
import { PageHeader, PageLayout } from '../../../components/ui';

interface UploadedFile {
  file: File;
  base64Data: string;
  sheetNames: string[];
  selectedSheet: string;
}

interface TestFile {
  file: File;
  base64Data: string;
  sheetNames: string[];
  selectedSheet: string;
}

const SuperadminGenerateIntegration = () => {
  const [receiverName, setReceiverName] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [generating, setGenerating] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  const [extraInstructions, setExtraInstructions] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Test section state
  const [testFile, setTestFile] = useState<TestFile | null>(null);
  const [exporters, setExporters] = useState<Exporter[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [selectedExporterId, setSelectedExporterId] = useState('');
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<SettlementDetail[] | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const testFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getAllExporters()
      .then(setExporters)
      .catch((err) => {
        console.error('Error fetching exporters:', err);
        toast.error('Error al cargar exportadores');
      });
    getAllSpecies()
      .then(setSpecies)
      .catch((err) => {
        console.error('Error fetching species:', err);
        toast.error('Error al cargar especies');
      });
  }, []);

  const handleFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles: UploadedFile[] = [];

    for (const file of Array.from(files)) {
      try {
        const base64Data = await fileToBase64(file);
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetNames = workbook.SheetNames;

        newFiles.push({
          file,
          base64Data,
          sheetNames,
          selectedSheet: sheetNames[0],
        });
      } catch (error) {
        toast.error(`Error al leer ${file.name}`);
        console.error(error);
      }
    }

    setUploadedFiles((prev) => [...prev, ...newFiles]);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSheetChange = (index: number, sheet: string) => {
    setUploadedFiles((prev) =>
      prev.map((f, i) => (i === index ? { ...f, selectedSheet: sheet } : f))
    );
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGenerate = async () => {
    if (!receiverName.trim()) {
      toast.error('Ingrese el nombre del receiver');
      return;
    }

    if (uploadedFiles.length === 0) {
      toast.error('Agregue al menos un archivo Excel');
      return;
    }

    setGenerating(true);
    setGeneratedCode('');

    try {
      const examples: ExcelExample[] = uploadedFiles.map((f) => ({
        base64Data: f.base64Data,
        selectedSheet: f.selectedSheet,
        filename: f.file.name,
      }));

      const result = await generateIntegration({
        receiverName: receiverName.trim().toUpperCase(),
        examples,
        extraInstructions: extraInstructions.trim() || undefined,
      });

      if (result.success && result.code) {
        setGeneratedCode(result.code);
        toast.success('Codigo generado correctamente');
      } else {
        throw new Error(result.error || 'Error desconocido');
      }
    } catch (error: any) {
      console.error('Error generating:', error);
      toast.error(error.message || 'Error al generar la integracion');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedCode);
      toast.success('Codigo copiado al portapapeles');
    } catch {
      toast.error('Error al copiar');
    }
  };

  const handleTestFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const base64Data = await fileToBase64(file);
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheetNames = workbook.SheetNames;

      setTestFile({
        file,
        base64Data,
        sheetNames,
        selectedSheet: sheetNames[0],
      });
    } catch (error) {
      toast.error(`Error al leer ${file.name}`);
      console.error(error);
    }

    if (testFileInputRef.current) {
      testFileInputRef.current.value = '';
    }
  };

  const handleTest = async () => {
    if (!testFile) {
      toast.error('Agregue un archivo Excel para probar');
      return;
    }
    if (!receiverName.trim()) {
      toast.error('Ingrese el nombre del receiver');
      return;
    }
    if (!selectedExporterId) {
      toast.error('Seleccione un exportador');
      return;
    }
    if (!selectedSpecies) {
      toast.error('Seleccione una especie');
      return;
    }

    setTesting(true);

    try {
      // Build a new workbook containing only the selected sheet
      // so the transformation function processes the correct one
      const binaryString = atob(testFile.base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const fullWorkbook = XLSX.read(bytes, { type: 'array' });
      const newWorkbook = XLSX.utils.book_new();
      const sheet = fullWorkbook.Sheets[testFile.selectedSheet];
      XLSX.utils.book_append_sheet(newWorkbook, sheet, testFile.selectedSheet);
      const filteredBase64 = XLSX.write(newWorkbook, {
        type: 'base64',
        bookType: 'xlsx',
      });

      const result = await processExcelFile({
        excelData: filteredBase64,
        filename: testFile.file.name,
        receiverName: receiverName.trim().toUpperCase(),
        exporterId: Number(selectedExporterId),
        speciesId: selectedSpecies.id,
      });

      if (result.success && result.data) {
        setTestResult(result.data);
        setIsDialogOpen(true);
        toast.success(`Test exitoso: ${result.data.length} filas procesadas`);
      } else {
        throw new Error(result.error || 'Error desconocido');
      }
    } catch (error: any) {
      console.error('Error testing:', error);
      toast.error(error.message || 'Error al probar la integracion');
    } finally {
      setTesting(false);
    }
  };

  return (
    <PageLayout>
      <PageHeader title='Generar Integracion' />

      <Card className='mb-6'>
        <CardBody className='space-y-6 p-6'>
          <Input
            label='Nombre del Receiver'
            placeholder='Ej: FRUITLINK, RIVERKING, etc.'
            value={receiverName}
            onChange={(e) => setReceiverName(e.target.value.toUpperCase())}
            className='max-w-md'
          />

          <div>
            <p className='text-sm text-default-500 mb-2'>
              Archivos Excel de ejemplo ({uploadedFiles.length} agregado
              {uploadedFiles.length !== 1 ? 's' : ''})
            </p>

            <input
              ref={fileInputRef}
              type='file'
              multiple
              accept='.xlsx,.xls'
              onChange={handleFilesSelected}
              className='hidden'
            />

            <Button
              variant='flat'
              onPress={() => fileInputRef.current?.click()}
              startContent={<Icon icon='tabler:upload' width={18} />}
            >
              Agregar Archivos
            </Button>
          </div>

          {uploadedFiles.length > 0 && (
            <div className='space-y-3'>
              {uploadedFiles.map((uf, index) => (
                <div
                  key={index}
                  className='flex items-center gap-4 p-3 bg-default-100 rounded-lg'
                >
                  <Icon
                    icon='tabler:file-spreadsheet'
                    width={24}
                    className='text-green-600 dark:text-green-400 shrink-0'
                  />

                  <span className='text-sm font-medium truncate flex-1'>
                    {uf.file.name}
                  </span>

                  <select
                    value={uf.selectedSheet}
                    onChange={(e) => handleSheetChange(index, e.target.value)}
                    className='text-sm border rounded-lg px-2 py-1 bg-background'
                  >
                    {uf.sheetNames.map((sheet) => (
                      <option key={sheet} value={sheet}>
                        {sheet}
                      </option>
                    ))}
                  </select>

                  <Button
                    isIconOnly
                    size='sm'
                    variant='light'
                    color='danger'
                    onPress={() => handleRemoveFile(index)}
                  >
                    <Icon icon='tabler:x' width={16} />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <Textarea
            label='Instrucciones adicionales (opcional)'
            placeholder='Ej: La columna H tiene el formato y peso combinados, la fecha esta en la celda B2...'
            value={extraInstructions}
            onChange={(e) => setExtraInstructions(e.target.value)}
            minRows={2}
            maxRows={6}
          />

          <div className='flex justify-end'>
            <Button
              color='primary'
              onPress={handleGenerate}
              isLoading={generating}
              isDisabled={!receiverName.trim() || uploadedFiles.length === 0}
            >
              Generar Funcion
            </Button>
          </div>
        </CardBody>
      </Card>

      {generatedCode && (
        <Card className='mb-6'>
          <CardBody className='p-6'>
            <div className='flex justify-between items-center mb-4'>
              <h2 className='text-xl font-semibold'>Codigo Generado</h2>
              <Button
                size='sm'
                variant='flat'
                onPress={handleCopy}
                startContent={<Icon icon='tabler:clipboard' width={16} />}
              >
                Copiar
              </Button>
            </div>

            <pre className='bg-default-100 text-foreground p-4 rounded-lg overflow-x-auto text-sm leading-relaxed'>
              <code>{generatedCode}</code>
            </pre>
          </CardBody>
        </Card>
      )}

      {/* Test Section */}
      <Card>
        <CardBody className='space-y-6 p-6'>
          <h2 className='text-xl font-semibold'>Probar Integracion</h2>
          <p className='text-sm text-default-500'>
            Sube un Excel para probar la funcion desplegada en excel-process.
          </p>

          {/* Test file upload */}
          <div>
            <input
              ref={testFileInputRef}
              type='file'
              accept='.xlsx,.xls'
              onChange={handleTestFileSelected}
              className='hidden'
            />

            {!testFile ? (
              <Button
                variant='flat'
                onPress={() => testFileInputRef.current?.click()}
                startContent={<Icon icon='tabler:upload' width={18} />}
              >
                Subir Excel de prueba
              </Button>
            ) : (
              <div className='flex items-center gap-4 p-3 bg-default-100 rounded-lg'>
                <Icon
                  icon='tabler:file-spreadsheet'
                  width={24}
                  className='text-green-600 dark:text-green-400 shrink-0'
                />
                <span className='text-sm font-medium truncate flex-1'>
                  {testFile.file.name}
                </span>
                <select
                  value={testFile.selectedSheet}
                  onChange={(e) =>
                    setTestFile((prev) =>
                      prev ? { ...prev, selectedSheet: e.target.value } : prev
                    )
                  }
                  className='text-sm border rounded-lg px-2 py-1 bg-background'
                >
                  {testFile.sheetNames.map((sheet) => (
                    <option key={sheet} value={sheet}>
                      {sheet}
                    </option>
                  ))}
                </select>
                <Button
                  isIconOnly
                  size='sm'
                  variant='light'
                  color='danger'
                  onPress={() => setTestFile(null)}
                >
                  <Icon icon='tabler:x' width={16} />
                </Button>
              </div>
            )}
          </div>

          {/* Exporter and Species selects */}
          <div className='flex gap-4'>
            <Select
              label='Exportador'
              placeholder='Seleccionar exportador'
              selectedKeys={selectedExporterId ? [selectedExporterId] : []}
              onSelectionChange={(keys) => {
                const key = Array.from(keys)[0]?.toString() || '';
                setSelectedExporterId(key);
              }}
              className='max-w-xs'
            >
              {exporters.map((exp) => (
                <SelectItem key={exp.id.toString()} value={exp.id.toString()}>
                  {exp.name}
                </SelectItem>
              ))}
            </Select>

            <Select
              label='Especie'
              placeholder='Seleccionar especie'
              selectedKeys={selectedSpecies ? [selectedSpecies.id.toString()] : []}
              onSelectionChange={(keys) => {
                const key = Array.from(keys)[0]?.toString() || '';
                const found = species.find((s) => s.id.toString() === key) || null;
                setSelectedSpecies(found);
              }}
              className='max-w-xs'
            >
              {species.map((sp) => (
                <SelectItem key={sp.id.toString()} value={sp.id.toString()}>
                  {sp.name}
                </SelectItem>
              ))}
            </Select>
          </div>

          <div className='flex justify-end'>
            <Button
              color='primary'
              onPress={handleTest}
              isLoading={testing}
              isDisabled={
                !testFile ||
                !receiverName.trim() ||
                !selectedExporterId ||
                !selectedSpecies
              }
            >
              Probar
            </Button>
          </div>
        </CardBody>
      </Card>

      <UploadSettlementDialog
        isManualUpload={false}
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setTestResult(null);
        }}
        excelData={testResult}
        onSuccess={() => {
          setIsDialogOpen(false);
          setTestResult(null);
          toast.success('Test completado');
        }}
        selectedSpecies={selectedSpecies}
        preferredReceiverName={receiverName.trim().toUpperCase()}
      />
    </PageLayout>
  );
};

export default SuperadminGenerateIntegration;
